/**
 * The fields of the exposed parameters, generated from the params schema of the image version
 * (see params-form.js). Controlled: it shows `value` and `errors`, and reports every edit with a
 * `params-change` event carrying the new params; the page owns the state.
 *
 * `assistants` are the other assistants of the workspace (`{ id, name }`), those a field of the
 * assistants kind chooses among; without it, as for a template, which is one assistant, such a
 * field is not shown.
 */
import { LitElement, html } from 'lit';

import { fieldsOf, getAt, parseList, setAt } from '../params-form.js';
import { formStyles } from '../../styles/shared/forms.js';
import { planeParamsFormStyles } from '../../styles/components/plane-params-form.js';

class PlaneParamsForm extends LitElement {
  static properties = {
    schema: { type: Object },
    value: { type: Object },
    errors: { type: Object },
    disabled: { type: Boolean },
    assistants: { type: Array },
  };

  static styles = [
    formStyles,
    planeParamsFormStyles,
  ];

  constructor() {
    super();
    this.value = {};
    this.errors = {};
  }

  change(path, value) {
    this.dispatchEvent(new CustomEvent('params-change', { detail: { value: setAt(this.value, path, value) }, bubbles: true, composed: true }));
  }

  renderControl(field) {
    const id = `field-${field.key}`;
    const current = getAt(this.value, field.path);
    switch (field.kind) {
      case 'select':
        return html`
          <select id=${id} ?disabled=${this.disabled} @change=${(event) => this.change(field.path, event.target.value || undefined)}>
            ${field.required
              ? // without it the browser shows the first option as chosen while the value is still unset
                html`<option value="" disabled ?selected=${current === undefined}>Choose…</option>`
              : html`<option value="" ?selected=${current === undefined}>Default</option>`}
            ${field.options.map((option) => html`<option value=${option} ?selected=${option === current}>${option}</option>`)}
          </select>
        `;
      case 'boolean':
        return html`
          <label class="checkbox">
            <input
              id=${id}
              type="checkbox"
              ?disabled=${this.disabled}
              .checked=${current === true}
              @change=${(event) => this.change(field.path, event.target.checked)}
            />
            Yes
          </label>
        `;
      case 'assistants': {
        const chosen = current ?? [];
        if (this.assistants.length === 0) {
          return html`<p class="help" id=${id}>No other assistant in the workspace yet.</p>`;
        }
        return html`
          <div class="choices" id=${id} role="group">
            ${this.assistants.map(
              (assistant) => html`
                <label class="checkbox">
                  <input
                    type="checkbox"
                    ?disabled=${this.disabled}
                    .checked=${chosen.includes(assistant.id)}
                    @change=${(event) => {
                      const next = event.target.checked ? [...chosen, assistant.id] : chosen.filter((other) => other !== assistant.id);
                      this.change(field.path, next.length > 0 ? next : undefined);
                    }}
                  />
                  ${assistant.name} <code>${assistant.id}</code>
                </label>
              `,
            )}
          </div>
        `;
      }
      case 'multiline':
        return html`
          <textarea
            id=${id}
            rows="3"
            maxlength=${field.schema.maxLength ?? ''}
            ?disabled=${this.disabled}
            .value=${current ?? ''}
            @input=${(event) => this.change(field.path, event.target.value === '' ? undefined : event.target.value)}
          ></textarea>
        `;
      case 'list':
        return html`
          <textarea
            id=${id}
            rows="3"
            ?disabled=${this.disabled}
            .value=${(current ?? []).join('\n')}
            @change=${(event) => this.change(field.path, parseList(event.target.value))}
          ></textarea>
        `;
      case 'integer':
        return html`
          <input
            id=${id}
            type="text"
            inputmode="numeric"
            ?disabled=${this.disabled}
            .value=${current ?? ''}
            @input=${(event) => this.change(field.path, event.target.value === '' ? undefined : Number(event.target.value))}
          />
        `;
      case 'image':
        return html`
          <plane-avatar-input
            id=${id}
            .value=${current}
            .label=${getAt(this.value, ['agentName'])}
            .maxLength=${field.schema.maxLength}
            ?disabled=${this.disabled}
            @avatar-change=${(event) => this.change(field.path, event.detail.value)}
          ></plane-avatar-input>
        `;
      default:
        return html`
          <input
            id=${id}
            type="text"
            ?disabled=${this.disabled}
            .value=${current ?? ''}
            @input=${(event) => this.change(field.path, event.target.value === '' ? undefined : event.target.value)}
          />
        `;
    }
  }

  renderField(field) {
    const problem = this.errors?.[field.key];
    return html`
      <div class="field ${problem ? 'invalid' : ''}">
        ${field.kind === 'boolean'
          ? html`<span class="label">${field.title}</span>`
          : html`<label for="field-${field.key}">${field.title}${field.required ? html` <span class="required" aria-hidden="true">*</span>` : null}</label>`}
        ${field.description ? html`<span class="help">${field.description}</span>` : null} ${this.renderControl(field)}
        ${problem ? html`<span class="problem" role="alert">${problem}</span>` : null}
      </div>
    `;
  }

  render() {
    if (!this.schema) {
      return null;
    }
    // a choice among the other assistants means nothing outside a workspace
    const fields = fieldsOf(this.schema).filter((field) => field.kind !== 'assistants' || this.assistants !== undefined);
    const groups = [];
    for (const field of fields) {
      const last = groups.at(-1);
      if (last && last.title === field.group) {
        last.fields.push(field);
      } else {
        groups.push({ title: field.group, fields: [field] });
      }
    }
    // errors on fields the form does not show (unknown fields): said once, at the top
    const shown = new Set(fields.map((field) => field.key));
    const others = Object.entries(this.errors ?? {}).filter(([key]) => !shown.has(key));
    return html`
      ${others.map(([key, message]) => html`<p class="form-error" role="alert"><code>${key}</code>: ${message}</p>`)}
      ${groups.map((group) =>
        group.title
          ? html`<fieldset><legend>${group.title}</legend>${group.fields.map((field) => this.renderField(field))}</fieldset>`
          : group.fields.map((field) => this.renderField(field)),
      )}
    `;
  }
}

customElements.define('plane-params-form', PlaneParamsForm);
